/**
 * EventDetail — one AI event, read in a glance and WITHOUT scrolling.
 *
 * The old detail panel was a narrow column: snapshot on top, then every column of the table
 * repeated underneath as an undifferentiated label/value grid. Two things were wrong with it.
 * The image — the reason the record was opened — arrived small, and the three facts an operator
 * actually acts on (WHEN, WHO, IN or OUT) were mixed in with track ids and confidences, so
 * finding them meant reading the whole grid and scrolling past it.
 *
 * So: snapshots take one side at full width, and the facts split in two. `facts` are the few
 * that answer "what happened" and get tiles big enough to read across a desk; `fields` are the
 * remaining evidence, small and dense. Everything is sized to fit one screen at the modal's
 * `full` size — a record you have to scroll is a record you have to read twice.
 *
 * Presentational only, and a slot for the media: the local app resolves snapshots to URLs while
 * the cloud streams each frame through the node tunnel, and that difference must not fork the
 * layout into two implementations of "event detail".
 *
 * LAID OUT AS THE ALERT RECORD IS (AlertRecord): the same header (model · "Event #N · 15h ago",
 * the name, camera · time, the host's pager / "Full page" and close), the same Frame · Clip ±10s ·
 * Go live · Download bar under the picture, the same ruled label/value rows on the right and the
 * same bar across the foot. An operator moves between alerts and the events behind them all day;
 * two records of one detection should not have to be read two ways. The header, the pills and
 * the media bar are the alert record's own components, not copies of them.
 */
import { useState, type ComponentType, type ReactNode, type SVGProps } from 'react';
import {
  CheckIcon,
  ClipboardDocumentIcon,
  CpuChipIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { AlertMedia, RecordHeader, RecordTintPill, type AlertPlayback, type RecordFrameSlot } from '../alerts/AlertRecord';
import { reportModelLabel } from '../../constants/reportModels';

export type EventFactTone = 'default' | 'good' | 'warn' | 'bad';

export interface EventFact {
  key: string;
  label: string;
  value: ReactNode;
  tone?: EventFactTone;
  /** Ids and plate numbers read better monospaced. */
  mono?: boolean;
  /** The column type it came from — picks the headline tile's icon. */
  kind?: string;
  /**
   * Long free text — what OCR read off a sign, say. Spans the whole grid row and wraps onto as
   * many lines as it needs instead of being cut off at "…": a read the reader cannot see in full
   * is a read they have to open the raw record to check.
   */
  wide?: boolean;
}

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

/** "—" is the record saying "not read" — such facts are left out of the record entirely. */
const isBlank = (v: ReactNode) => v === '—' || v === '' || v == null;

/**
 * What the model called this event — "Face Recognized", "Loitering", "No-Helmet" — Title Cased
 * out of whichever field carries it. Empty when the row has none, which the title treats as
 * "the model's own name is the most specific thing we can say".
 */
export function eventTypeLabel(event: any): string {
  const raw: string = event?.sub_type || event?.event_type || event?.violation_type || '';
  return raw ? raw.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : '';
}

/** The shape of the per-model table column definitions both apps already carry. */
export interface EventColumnLike {
  key: string;
  label: string;
  type?: string;
  render?: (value: any, row?: any) => any;
  /** Show this column's value full-width and wrapped in the record's Details (see EventFact). */
  wide?: boolean;
}

export interface EventValueContext {
  /** camera id → display name. */
  cameraName?: (id: string) => string | undefined;
  /** The registered identity behind a face event, if it has one. */
  personName?: (event: any) => string | null | undefined;
}

const TONE_TEXT: Record<EventFactTone, string> = {
  default: 'text-foreground',
  good: 'text-green-400',
  warn: 'text-amber-400',
  bad: 'text-red-400',
};

const TONE_BADGE: Record<EventFactTone, string> = {
  default: 'bg-muted text-foreground',
  good: 'bg-green-500/15 text-green-500',
  warn: 'bg-amber-500/15 text-amber-500',
  bad: 'bg-red-500/15 text-red-500',
};

function Badge({ text, tone = 'default' }: { text: string; tone?: EventFactTone }) {
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-bold uppercase ${TONE_BADGE[tone]}`}>
      {text}
    </span>
  );
}

/**
 * Render ONE column's value for the detail view, with the tone that goes with it.
 *
 * Shares the table's vocabulary (`col.type`) so a value can't say one thing in the row and
 * another in the record — but not the table's sizing: a badge that has to fit a 13px cell is
 * free to be bigger here.
 */
export function renderEventValue(
  col: EventColumnLike,
  event: any,
  ctx: EventValueContext = {},
): { node: ReactNode; tone: EventFactTone; mono?: boolean } {
  const val = event?.[col.key];
  const plain = (node: ReactNode, tone: EventFactTone = 'default', mono?: boolean) => ({ node, tone, mono });

  switch (col.type) {
    case 'camera':
      return plain(ctx.cameraName?.(val) || (typeof val === 'string' ? val.slice(0, 8) : '') || '—');

    case 'face_identity': {
      /**
       * The resolver first, then the COLUMN'S OWN VALUE.
       *
       * That fallback is not belt-and-braces, it is the fix for a false negative. This case
       * used to read the resolver and nothing else, so any caller that did not supply one
       * reported every matched face as "Unknown" — the Search page's record modal did exactly
       * that, and a search FOR somebody by name opened a record denying it was them. The
       * column is `matched_person_name`; when the record already carries the name, no resolver
       * is needed to read it.
       *
       * The resolver still wins where there is one: it can reach the people registry for an
       * event that stored only a `person_id`, which this value cannot.
       */
      const name = ctx.personName?.(event) || (typeof val === 'string' && val ? val : null);
      return name ? plain(name, 'good') : plain('Unknown', 'warn');
    }
    case 'person_identity':
      return val ? plain(val, 'good') : plain('—');

    case 'face_status':
      return val ? plain(<Badge text="Known" tone="good" />, 'good') : plain(<Badge text="Unknown" tone="warn" />, 'warn');

    case 'inout':
      if (val === 'in') return plain(<Badge text="↓ In" tone="good" />, 'good');
      if (val === 'out') return plain(<Badge text="↑ Out" tone="bad" />, 'bad');
      return plain('—');

    case 'vehicle_direction':
      return val === 'entry'
        ? plain(<Badge text="↓ Entry" tone="good" />, 'good')
        : plain(<Badge text="↑ Exit" tone="warn" />, 'warn');

    case 'person_id': {
      // The registered identity's id, not the AI's per-track detection id, which stops
      // meaning anything once the face is registered.
      const raw = event?.matched_person_id || val;
      return raw ? plain(String(raw), 'default', true) : plain('—');
    }

    case 'plate_number':
      return val ? plain(String(val).toUpperCase(), 'default', true) : plain('—');

    case 'plate_status':
      if (!event?.has_anpr) return plain('No ANPR');
      return plain(
        <Badge text={val || 'detected'} tone={val === 'valid' ? 'good' : val === 'partial' ? 'warn' : 'bad'} />,
        val === 'valid' ? 'good' : val === 'partial' ? 'warn' : 'bad',
      );

    case 'ppe_violation_type': {
      const tone: EventFactTone = String(val || '').toLowerCase().includes('no-') ? 'bad' : 'warn';
      return plain(<Badge text={val || '—'} tone={tone} />, tone);
    }

    case 'theft_type': {
      const tone: EventFactTone = String(val || '').toLowerCase().includes('shoplifting') ? 'bad' : 'warn';
      return plain(<Badge text={val || '—'} tone={tone} />, tone);
    }

    case 'detection_type': {
      // Fire and smoke are one detector's two classes, and they are not the same
      // news: flame is 'bad', smoke is the warning that precedes it. A detector
      // that could not tell which it saw says so rather than guessing.
      const kind = String(val || '').toLowerCase();
      const tone: EventFactTone = kind === 'fire' ? 'bad' : 'warn';
      return plain(<Badge text={val || 'unknown'} tone={tone} />, tone);
    }

    default:
      if (col.render) return plain(col.render(val, event) ?? '—');
      return plain(val == null || val === '' ? '—' : String(val));
  }
}

/**
 * Which columns earn a headline tile, in the order they're read: when it happened, who it was,
 * which way they went, what the model called it, and where. Everything else is evidence.
 *
 * Keyed off the table's own column vocabulary rather than a per-model list, so a model gaining
 * a column gets the right treatment without another place to edit.
 */
function headlineRank(col: EventColumnLike): number {
  if (col.key === 'event_timestamp') return 0;
  switch (col.type) {
    case 'face_identity':
    case 'person_identity':
      return 1;
    case 'inout':
    case 'vehicle_direction':
      return 2;
    case 'plate_number':
    case 'ppe_violation_type':
    case 'theft_type':
    // Fire or smoke is the first thing read off a fire event and decides what the
    // operator does next, so it earns a headline tile rather than a line in the
    // supporting fields.
    case 'detection_type':
      return 3;
    case 'camera':
      return 4;
    default:
      return -1;
  }
}

/** Columns that are the table's own furniture rather than facts about the event. */
const NON_FACT_TYPES = ['images', 'face_action'];

/**
 * Split a model's columns into the headline facts and the rest, both already rendered.
 * One implementation for the modal and the routed page, in both apps.
 */
export function splitEventFacts(
  event: any,
  columns: EventColumnLike[],
  ctx: EventValueContext = {},
): { facts: EventFact[]; fields: EventFact[] } {
  const facts: (EventFact & { rank: number })[] = [];
  const fields: EventFact[] = [];

  for (const col of columns) {
    if (col.type && NON_FACT_TYPES.includes(col.type)) continue;
    const { node, tone, mono } = renderEventValue(col, event, ctx);
    const rank = headlineRank(col);
    if (rank >= 0) facts.push({ key: col.key, label: col.label, value: node, tone, mono, kind: col.type, rank });
    else fields.push({ key: col.key, label: col.label, value: node, tone, mono, kind: col.type, wide: col.wide });
  }

  facts.sort((a, b) => a.rank - b.rank);
  return { facts: facts.map(({ rank, ...f }) => f), fields };
}

export interface EventDetailProps {
  /** The record itself — for the header, the clip window and the download's filename. */
  event: any;
  /** The model's key (`face`, `fire_smoke`) — names the record in its header. */
  model?: string;
  cameraName?: string;
  /**
   * The snapshot viewer — each app builds its own, because each loads images differently.
   * A function is handed the frame height and a selection callback (see RecordFrameSlot), so
   * Download saves the frame on screen.
   */
  media?: ReactNode | ((slot: RecordFrameSlot) => ReactNode);
  /** Recorded footage of the camera, for "Clip ±10s". Null hides nothing — the tab disables. */
  playback?: AlertPlayback | null;
  /** The camera live, for "Go live". Null leaves the button out. */
  renderLive?: (() => ReactNode) | null;
  /** In a modal: the pager and "Full page", drawn in the record's own header. */
  headerActions?: ReactNode;
  /** In a modal: the record's close button. */
  onClose?: () => void;
  /**
   * One line under the snapshots. Used to say what the arrow keys do where BOTH the record and
   * the snapshots respond to them — a contract nobody would guess from looking.
   */
  hint?: ReactNode;
  /** Shown large: the few facts the operator acts on. */
  facts: EventFact[];
  /** Shown small and dense: the supporting evidence. */
  fields: EventFact[];
  /**
   * WHOSE APPEARANCE THE PICTURE IS SHOWING — its own titled block, above the event's fields.
   *
   * Appearance is a property of a SUBJECT, and the frame beside it shows one of them; the fields
   * below describe the EVENT, which is a different scope with different answers. They were one
   * grid, so a record of a ten-person frame printed one person's shirt colour under a picture of
   * whoever the reader had clicked to — usually somebody else. Separating them is what lets both
   * be true at once, and the block names who it is about.
   */
  subjectPanel?: ReactNode;
  /**
   * A block below the dense fields, for what neither a tile nor a label/value pair can hold.
   *
   * In practice: the frame's subject breakdown — one row per person or vehicle the event saw.
   * It is a LIST of several things each with several attributes, so it cannot be a `field`
   * (a two-column label/value grid) without being flattened into a sentence nobody can scan.
   */
  extra?: ReactNode;
  /** Buttons that act on the event (register this face, …). */
  actions?: ReactNode;
  /**
   * A standing statement about the record rather than a fact of it — "matched against the
   * registered face vector set". Sits in the footer bar opposite the actions, where it says
   * how the record was arrived at without competing with the facts for the reader's eye.
   */
  note?: ReactNode;
  className?: string;
}

/**
 * One section heading, so every block in the record is introduced the same way.
 *
 * The record grew three blocks that each labelled themselves — "Details", "Appearance", "In
 * this frame" — in three slightly different ways, and a reader scanning down a column reads
 * those differences as meaning something. They mean nothing; they were three authors.
 */
export function SectionHeading({ children, chip, aside, icon: HeadIcon, className = 'mb-2.5' }: {
  children: ReactNode;
  /** A small icon before the title — the block's kind, at a glance. */
  icon?: Icon;
  className?: string;
  /** The block's own answer, when it has a short one — "7 people", "#3 · A. Rao". */
  chip?: ReactNode;
  /** A quiet note on the right: what the block is about, or what to do with it. */
  aside?: ReactNode;
}) {
  return (
    <h3 className={`flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground ${className}`}>
      {HeadIcon && <HeadIcon className="h-4 w-4 shrink-0 text-brand" />}
      <span className="text-foreground">{children}</span>
      {chip && (
        <span className="rounded-md bg-brand-light px-1.5 py-0.5 text-[11px] font-semibold normal-case tracking-normal text-brand">
          {chip}
        </span>
      )}
      {aside && (
        <span className="text-[10px] font-normal normal-case tracking-normal text-muted-foreground">
          {aside}
        </span>
      )}
    </h3>
  );
}

/** Copy an id without selecting a truncated string by hand. */
function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(text).then(() => {
          setDone(true);
          window.setTimeout(() => setDone(false), 1200);
        }).catch(() => { /* insecure context — the value is still in the title */ });
      }}
      title={done ? 'Copied' : 'Copy'}
      aria-label={`Copy ${text}`}
      className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      {done ? <CheckIcon className="h-3.5 w-3.5 text-green-500" /> : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
    </button>
  );
}

/**
 * A label/value grid — the shape both the record's own fields and a subject's read in.
 *
 * Each pair sits on a small tile of its own: as bare text in two columns the pairs ran together
 * and the eye could not tell a label from the value above it. `tile` is the tile surface, which
 * differs by the panel it sits on (card on the tinted Appearance panel, muted on a card).
 */
function FactGrid({ items, tile = 'bg-muted' }: { items: EventFact[]; tile?: string }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {items.map((f) => {
        const blank = isBlank(f.value);
        const copyable = f.mono && typeof f.value === 'string' && !blank;
        return (
          <div key={f.key} className={`flex min-w-0 flex-col gap-1 rounded-lg px-3 py-2 ${tile} ${f.wide ? 'col-span-2' : ''}`}>
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {f.label}
            </span>
            <span className="flex min-w-0 items-center gap-1">
              <span
                className={`min-w-0 ${f.wide ? 'whitespace-pre-line break-words' : 'truncate'} text-[14px] font-medium ${
                  blank ? 'text-muted-foreground' : TONE_TEXT[f.tone || 'default']
                } ${f.mono ? 'font-mono text-[13px]' : ''}`}
                title={typeof f.value === 'string' ? f.value : undefined}
              >
                {f.value}
              </span>
              {copyable && <CopyButton text={f.value as string} />}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * WHOSE APPEARANCE THE PICTURE IS SHOWING — the block that goes in `subjectPanel`.
 *
 * Shared rather than written out in each app because both build the identical thing from the
 * identical parts, and two copies of a block whose whole job is to agree with the picture beside
 * it is two chances to disagree with it.
 *
 * BRAND-TINTED, and that is the point rather than decoration: the selected entry in the picker,
 * the chip over the frame and this panel are one claim said three times — "this is the crop you
 * are looking at, and this is who it is". A reader who cannot pair them has to work out which of
 * seven people the words are about from the picture alone.
 */
export function AppearancePanel({ facts, subjectSeq, subjectLabel, ofFrame }: {
  facts: EventFact[];
  /** The module's own index for the subject — the number their crop is named after. */
  subjectSeq?: number | null;
  /** Their resolved identity, where there is one: a name, or a plate. */
  subjectLabel?: string | null;
  /**
   * True when these are the attributes of the crop ON SCREEN, false when they are the subject
   * the record was OPENED from — which is the honest answer under a wide shot, where no one
   * person is being shown. The reader is told which, because they are different claims.
   */
  ofFrame: boolean;
}) {
  // Only what was read: a grid of "—" says nothing, and a panel of only that is not shown.
  facts = facts.filter((f) => !isBlank(f.value));
  if (!facts.length) return null;
  return (
    /* The brand tint moved into a header band with a matching left edge: a whole panel of it
       was loud enough to out-shout the picture it describes, and the pairing with the selected
       crop only needs the colour, not a wall of it. */
    <section className="overflow-hidden rounded-xl border border-border border-l-4 border-l-brand bg-card shadow-sm">
      <div className="border-b border-border bg-brand-light px-3.5 py-2.5">
        <SectionHeading
          icon={UserIcon}
          className=""
          chip={subjectSeq != null
            ? `#${subjectSeq}${subjectLabel ? ` · ${subjectLabel}` : ''}`
            : undefined}
          aside={ofFrame ? 'the crop shown on the left' : 'the subject this record was opened from'}
        >
          Appearance
        </SectionHeading>
      </div>
      <div className="p-3">
        <FactGrid items={facts} />
      </div>
    </section>
  );
}

/** Arrow characters in a hint drawn as keycaps, so "← →" reads as keys to press, not prose. */
function withKeys(text: string): ReactNode[] {
  return text.split(/([←→↑↓])/).map((part, i) => (
    /^[←→↑↓]$/.test(part)
      ? (
        <kbd
          key={i}
          className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-border bg-card px-1 font-sans text-[11px] font-semibold text-foreground shadow-sm"
        >
          {part}
        </kbd>
      )
      : <span key={i}>{part}</span>
  ));
}

/** "Event #4821" — a long id (a uuid) is cut to the part a person would read out. */
function eventReference(event: any): string | null {
  const id = event?.id ?? event?.event_id;
  if (id == null || id === '') return null;
  const text = String(id);
  return `Event #${text.length > 12 ? text.slice(0, 8) : text}`;
}

/**
 * The event record's header — AlertRecordHeader's twin, on the same RecordHeader. The model is
 * the brand-tinted pill where an alert has its severity; the title is what the model called
 * the event, or "<model> event" where that is only the model's name again.
 */
export function EventRecordHeader({ event, model, cameraName, actions, onClose, fallbackTitle }: {
  /** Null while loading: the header still renders its actions and close, so the modal can be left. */
  event: any | null;
  model?: string;
  cameraName?: string;
  actions?: ReactNode;
  onClose?: () => void;
  /** Shown in place of the skeleton when there is no event to describe (it failed to load). */
  fallbackTitle?: ReactNode;
}) {
  const modelLabel = reportModelLabel(model);
  const type = eventTypeLabel(event);
  const distinct = type && type.toLowerCase() !== modelLabel.toLowerCase();
  return (
    <RecordHeader
      ready={!!event}
      stripe="var(--brand)"
      pills={event && (
        <RecordTintPill color="var(--brand)" icon={<CpuChipIcon className="h-4 w-4" />}>
          {modelLabel}
        </RecordTintPill>
      )}
      reference={event ? eventReference(event) : null}
      title={event ? (distinct ? type : `${modelLabel} event`) : fallbackTitle}
      cameraName={cameraName || event?.camera_id}
      timestamp={event?.event_timestamp || event?.created_at}
      mono={event?.event_type || null}
      actions={actions}
      onClose={onClose}
    />
  );
}

/** A block of the record's right-hand column, headed the way the alert record heads its own. */
function RecordSection({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-1 flex flex-wrap items-baseline gap-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {title}
        {aside && <span className="text-[11px] font-normal normal-case tracking-normal">{aside}</span>}
      </h3>
      {children}
    </section>
  );
}

/**
 * Facts as ruled label/value rows — the alert record's AlertFactList, keeping what an event's
 * facts carry that an alert's do not: the tone (in/out, known/unknown), monospaced ids with a
 * copy button, and long free text (`wide`) wrapped in full instead of cut at "…".
 */
function FactRows({ items }: { items: EventFact[] }) {
  return (
    <dl>
      {items.map((f) => {
        const copyable = f.mono && typeof f.value === 'string';
        return (
          <div key={f.key} className="grid grid-cols-[minmax(6rem,9rem)_minmax(0,1fr)] gap-3 border-b border-border py-2.5">
            <dt className="text-[15px] text-muted-foreground">{f.label}</dt>
            <dd className="flex min-w-0 items-center gap-1">
              <span
                className={`min-w-0 ${f.wide ? 'whitespace-pre-line' : ''} break-words font-medium ${
                  f.mono ? 'font-mono text-[14px]' : 'text-[15px]'
                } ${TONE_TEXT[f.tone || 'default']}`}
              >
                {f.value}
              </span>
              {copyable && <CopyButton text={f.value as string} />}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export default function EventDetail({
  event, model, cameraName, media, playback, renderLive, headerActions, onClose,
  hint, facts, fields, subjectPanel, extra, actions, note, className = '',
}: EventDetailProps) {
  // A fact with no value is not shown at all — "In/Out —" and "People —" were rows of nothing,
  // and they pushed the facts that DO have an answer further from the eye.
  facts = facts.filter((f) => !isBlank(f.value));
  fields = fields.filter((f) => !isBlank(f.value));
  const modelLabel = reportModelLabel(model);
  const type = eventTypeLabel(event);
  return (
    <div className={`flex min-h-full flex-col bg-card ${className}`}>
      <EventRecordHeader event={event} model={model} cameraName={cameraName} actions={headerActions} onClose={onClose} />

      {/* The evidence on the left (the frames, the footage, the camera now), what was read off
          it on the right — the alert record's split. One column below xl. */}
      <div className="grid flex-1 xl:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)]">
        {/* Snapshots. First in the DOM as well as on screen: on a narrow window the columns
            stack, and the image is still what the record is about. */}
        <div className="min-w-0 space-y-3 px-6 py-5">
          <AlertMedia
            alert={event}
            name={type || modelLabel}
            cameraName={cameraName}
            images={[]}
            renderFrame={media ? (slot) => (typeof media === 'function' ? media(slot) : media) : null}
            emptyNote="No snapshots for this event"
            playback={playback}
            renderLive={renderLive}
            frameClassName="h-[42vh]"
          />
          {/* The hint is a HELP note, so it is drawn as one. As bare grey text under a picture
              it read as a caption — something the record was telling you about this event —
              and the two sentences it actually contains are about the keyboard. */}
          {hint && (
            <p className="flex flex-wrap items-center gap-x-1 gap-y-1 rounded-lg border border-border bg-muted px-3 py-1.5 text-[12px] leading-relaxed text-muted-foreground">
              {typeof hint === 'string' ? withKeys(hint) : hint}
            </p>
          )}
        </div>

        <div className="flex min-w-0 flex-col gap-6 border-t border-border px-6 py-5 xl:border-l xl:border-t-0">
          {/* What happened — when, who, which way, what the model called it, where. */}
          {facts.length > 0 && (
            <RecordSection title="Details">
              <FactRows items={facts} />
            </RecordSection>
          )}

          {/* Whose appearance the frame is showing — above the event's remaining fields,
              because it is about the picture the reader is looking at rather than the record. */}
          {subjectPanel}

          {fields.length > 0 && (
            <RecordSection title="More details" aside="everything else this record carries">
              <FactRows items={fields} />
            </RecordSection>
          )}
        </div>
      </div>

      {/* FULL WIDTH, below both columns — not inside the right one.
          The subject breakdown is a row of faces with a labelled grid beside each, and the
          right column is the narrow one. Squeezed in there the thumbnails and the attributes
          fight for the same 300px and neither is readable; across the whole record it is two
          cards wide and scannable. It also belongs last on its own merits: it describes the
          CONTEXT the record sits in — how many people were there, and who else — which is the
          question that follows reading the record rather than one competing with it. */}
      {extra && <div className="px-6 pb-5">{extra}</div>}

      {/* One bar across the whole record, pinned to the foot like the alert record's action
          bar: what acts on the event acts on all of it, and it stays reachable in a record that
          has been scrolled past its own actions. Opaque (an inline colour-mix, not a `/95`
          modifier — those compile to nothing on the theme's var() tokens), so the fields
          scrolling underneath never print through it. */}
      {(actions || note) && (
        <div
          className="sticky bottom-0 z-20 flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-3 border-t border-border px-6 py-4"
          style={{ backgroundColor: 'color-mix(in srgb, var(--muted) 35%, var(--card))' }}
        >
          <p className="min-w-0 flex-1 text-[14px] text-muted-foreground">{note}</p>
          {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
        </div>
      )}
    </div>
  );
}
